import { mergeMap, distinctUntilChanged, map } from 'rxjs/operators';
import { IDict } from '../../types/utility';
import { Maybe } from 'maybe-not';
import { DSCustomElement } from 'dry-suite';
import {
    buildLoaders,
    ITectonOutletAdaptorResponse,
    setupOutletIFrameWrapper,
    setupOutletIFrameLoading,
    setupOutletIFrameResizer,
    setupOutletSetTitleListener,
    setupOutletInfo
} from 'q2-tecton-common/lib/outlets/tecton-outlet-base';
import { IMessageBus, sendMessage } from 'q2-tecton-common/lib/utility/message-bus';
import addparamToURL from 'q2-tecton-common/lib/utility/addParamToURL';
import { LoadingOptions } from 'q2-tecton-common/lib/types/config';
import { checkRequiredPermissionsOnPlatform } from 'q2-tecton-common/lib/utility/check-permissions';

import { BehaviorSubject } from 'rxjs';

export interface IOutletResolutionData {
    element: HTMLElement;
    moduleId: string;
    initialParams: IDict<string>;
    loadingOptions?: LoadingOptions;
}

export const tectonFeatureOutletObservedAttrs = [
    { name: 'context', reflectAs: String },
    { name: 'contextValue', reflectAs: String }
];

export function tectonFeatureOutlet(
    messageBus: IMessageBus,
    outletPath: string,
    customElement: DSCustomElement,
    memorizedProps$: BehaviorSubject<IDict<any>>,
    resize?: () => void
): ITectonOutletAdaptorResponse {
    const placeholder = document.createElement('div') as HTMLElement;
    const DOM$ = new BehaviorSubject(placeholder);
    const settled$ = new BehaviorSubject(false);
    const effect$ = new BehaviorSubject(() => {
        return;
    });
    let moduleId: string;

    const getContent = function() {
        return {
            loaderWrapper: customElement.querySelector<HTMLDivElement>(
                "q2-section[slot='loading-wrapper']"
            )!,
            iframeEl: customElement.shadowRoot!.querySelector('iframe')!,
            minHeight: memorizedProps$.getValue().minHeight
        };
    };

    const getModuleId = function() {
        return moduleId;
    };

    const loadingSubscription = setupOutletIFrameLoading(messageBus, getModuleId, getContent);
    const resizerSubscription = setupOutletIFrameResizer(
        messageBus,
        getModuleId,
        getContent,
        resize
    );
    const titleSubscription = setupOutletSetTitleListener(messageBus, getModuleId, getContent);
    const outletInfoSubscription = setupOutletInfo(messageBus, getModuleId, getContent);
    const outletResolutionData$ = memorizedProps$.pipe(
        map(memorizedProps => {
            return {
                contextValue: memorizedProps.contextValue as string,
                minHeight: memorizedProps.minHeight as string
            };
        }),
        distinctUntilChanged(),
        mergeMap(({ contextValue, minHeight }) => {
            return featureHTML(messageBus, outletPath, contextValue, customElement);
        })
    );

    const teardown$ = new BehaviorSubject(() => {
        outletDataSubscription.unsubscribe();
        loadingSubscription.unsubscribe();
        resizerSubscription.unsubscribe();
        titleSubscription.unsubscribe();
        outletInfoSubscription.unsubscribe();
    });

    const outletDataSubscription = outletResolutionData$.subscribe(outletDataM => {
        outletDataM.map(outletData => {
            // set topLevel module Id to be available for subscriptions
            moduleId = outletData.moduleId;
            const loaderWrapper = document.createElement('q2-section') as DSCustomElement;
            const minHeight = memorizedProps$.getValue().minHeight;
            loaderWrapper.style.minHeight = minHeight || '0px';
            let loaders = [];

            if (outletData.loadingOptions) {
                loaders = buildLoaders(outletData.loadingOptions);
                const outlet = outletData.loadingOptions.outlet;
                outlet && outlet.style && loaderWrapper.setAttribute('style', outlet.style);
                outlet && outlet.classList && loaderWrapper.classList.add(...outlet.classList);
            } else {
                loaders = buildLoaders();
            }
            for (const loaderRow of loaders) {
                const row = document.createElement('div');
                row.className = 'q2-row';
                for (const loader of loaderRow) {
                    row.appendChild(loader);
                }
                loaderWrapper.appendChild(row);
            }
            loaderWrapper.slot = 'loading-wrapper';
            loaderWrapper.setAttribute('data-tecton-module', '');

            DOM$.next(outletData.element);
            effect$.next(() => {
                if (customElement.firstChild) {
                    customElement.replaceChild(loaderWrapper, customElement.firstChild!);
                } else {
                    customElement.appendChild(loaderWrapper);
                }
                settled$.next(true);
            });
            teardown$.next(() => {
                sendMessage(
                    'clearParams',
                    Maybe.just(JSON.stringify({ moduleId: outletData.moduleId }))
                );
            });
        });
    });

    return {
        DOM$,
        settled$,
        effect$,
        teardown$
    };
}

function featureHTML(
    messageBus: IMessageBus,
    outletPath: string,
    contextValue: string | null,
    customElement: DSCustomElement
): Promise<Maybe<IOutletResolutionData>> {
    return Promise.all([
        resolveFeatureOutletOnPlatform(messageBus, outletPath, contextValue),
        messageBus
            .sendRequest<{ key: string; value: string }[]>('getAuthPayload', Maybe.nothing())
            .then(r => r.value)
    ]).then(function([resolvedModule, authPayload]) {
        return checkRequiredPermissionsOnPlatform(
            messageBus,
            resolvedModule.requiredUserCapabilities
        ).then(allowed => {
            const resolvedModuleMaybe =
                !!resolvedModule.url && allowed
                    ? Maybe.just(resolvedModule)
                    : Maybe.nothing<IResolvedFeature>();

            return resolvedModuleMaybe.map(
                ({ url, moduleId, initialParams, formPostAuth, loadingOptions }) => {
                    const iframeSrc = addparamToURL(url, {
                        'tct-id': moduleId,
                        'tct-ctxid': contextValue || '',
                        ...initialParams,
                        'tct-maxHeight': customElement.style.maxHeight,
                        'tct-maxWidth': customElement.style.maxWidth
                    });
                    const iframeTitle = moduleId
                        .split('.')
                        .slice(-2)
                        .join('-');
                    const outletDOM = setupOutletIFrameWrapper(
                        iframeSrc,
                        moduleId,
                        authPayload,
                        iframeTitle,
                        customElement.style.maxHeight,
                        customElement.style.maxWidth,
                        false,
                        formPostAuth,
                        resolvedModule.additionalDomains,
                        resolvedModule.allowDirectives
                    );
                    return { element: outletDOM, moduleId, initialParams, loadingOptions };
                }
            );
        });
    });
}

export interface IResolvedFeature {
    moduleId: string;
    initialParams: IDict<string>;
    url: string;
    minHeight?: string;
    formPostAuth: boolean;
    requiredUserCapabilities?: string[] | undefined;
    loadingOptions?: LoadingOptions;
    additionalDomains?: string[] | undefined;
    allowDirectives?: string[] | undefined;
}

export function resolveFeatureOutletOnPlatform(
    messageBus: IMessageBus,
    outletPath: string,
    contextValue: string | null
): Promise<IResolvedFeature> {
    return messageBus
        .sendRequest<IResolvedFeature>(
            'resolveFeature',
            Maybe.just({
                outletPath,
                contextValue
            }).map(JSON.stringify)
        )
        .then(response => {
            return response.value;
        });
}
